import { useCallback, useState } from 'react';
import type { InternalTableSchema, InternalConfirmConfig } from '../core/schema/types';

function applyPayloadMapper(
  rows: Record<string, unknown>[],
  mapper: Readonly<Record<string, string>> | null,
): Record<string, unknown>[] {
  if (mapper == null) return rows;
  return rows.map((row) => {
    const mapped: Record<string, unknown> = {};
    for (const [payloadKey, rowField] of Object.entries(mapper)) {
      mapped[payloadKey] = row[rowField];
    }
    return mapped;
  });
}

interface UseTableSaveParams {
  internalSchema: InternalTableSchema;
  tableState: {
    editingRows: Map<string, Record<string, unknown>>;
    data: unknown[];
  };
  stateActions: {
    applyEdit: (rowKey: string) => void;
    cancelEditing: (rowKey: string) => void;
    cancelEditingBatch: (keys: string[]) => void;
    cancelAllEditing: () => void;
    clearServerErrors: () => void;
    setServerErrors: (errors: Map<string, Record<string, string>>) => void;
    setRowEditErrors?: (rowKey: string, errors: Record<string, string>) => void;
  };
  displayData: unknown[];
  resolveRowKey: (row: Record<string, unknown>, rowIndex: number) => string;
  getSubRowKeysForParent: (parentKey: string) => string[];
  onSave?: (rows: Record<string, unknown>[], allData: unknown[]) => Promise<void>;
  validateEditedRow?: (row: Record<string, unknown>) => Record<string, string>;
  isSaveLoading?: boolean;
  executeSaveAction: (
    rows: Record<string, unknown>[],
    endpoint: string,
    method?: string,
    onSuccess?: string,
    successMessage?: string
  ) => Promise<{ ok: boolean; errors?: Array<{ sNo: string | null; field: string; message: string }> } | undefined>;
}

interface UseTableSaveReturn {
  internalSaveLoading: boolean;
  effectiveSaveLoading: boolean;
  pendingSaveConfirm: {
    config: InternalConfirmConfig;
    onConfirm: () => void;
    onCancel: () => void;
    isLoading?: boolean;
  } | null;
  handleRowSave: (rowKey: string) => Promise<void>;
  handleRowCancel: (rowKey: string) => void;
  handleBulkSave: () => Promise<void>;
}

export function useTableSave({
  internalSchema,
  tableState,
  stateActions,
  displayData,
  resolveRowKey,
  getSubRowKeysForParent,
  onSave,
  validateEditedRow,
  isSaveLoading,
  executeSaveAction,
}: UseTableSaveParams): UseTableSaveReturn {
  const [internalSaveLoading, setInternalSaveLoading] = useState(false);
  const effectiveSaveLoading = isSaveLoading ?? internalSaveLoading;

  const [pendingSaveConfirm, setPendingSaveConfirm] = useState<{
    config: InternalConfirmConfig;
    onConfirm: () => void;
    onCancel: () => void;
    isLoading?: boolean;
  } | null>(null);

  const confirmSave = useCallback((): Promise<boolean> => {
    const cfg: InternalConfirmConfig = internalSchema.dataSource?.confirmBeforeSave ?? {
      title: 'Are you sure you want to Save?',
      message: '',
      confirmLabel: 'Confirm',
      cancelLabel: 'Cancel',
      variant: 'warning',
      titleAlign: 'center',
    };
    return new Promise<boolean>((resolve) => {
      setPendingSaveConfirm({
        config: cfg,
        onConfirm: () => {
          setPendingSaveConfirm((prev) => (prev ? { ...prev, isLoading: true } : null));
          resolve(true);
        },
        onCancel: () => {
          setPendingSaveConfirm(null);
          resolve(false);
        },
      });
    });
  }, [internalSchema.dataSource?.confirmBeforeSave]);

  const dismissSaveConfirm = useCallback(() => {
    setPendingSaveConfirm(null);
  }, []);

  const applyRowValidation = useCallback(
    (rows: Array<{ rowKey: string; mergedRow: Record<string, unknown> }>): boolean => {
      if (!validateEditedRow || !stateActions.setRowEditErrors) return true;

      const subField = internalSchema.features.rows.subRows?.dataField;
      let valid = true;
      for (const { rowKey, mergedRow } of rows) {
        const errors = validateEditedRow(mergedRow);
        stateActions.setRowEditErrors(rowKey, errors);
        if (Object.keys(errors).length > 0) {
          valid = false;
        }

        if (subField) {
          const subRows = (mergedRow[subField] as unknown[] | undefined) ?? [];
          for (let idx = 0; idx < subRows.length; idx++) {
            const subRowKey = `${rowKey}.${idx}`;
            if (!tableState.editingRows.has(subRowKey)) continue;
            const subRowRec = subRows[idx] as Record<string, unknown>;
            const subErrors = validateEditedRow(subRowRec);
            stateActions.setRowEditErrors(subRowKey, subErrors);
            if (Object.keys(subErrors).length > 0) {
              valid = false;
            }
          }
        }
      }
      return valid;
    },
    [validateEditedRow, stateActions, internalSchema.features.rows.subRows?.dataField, tableState.editingRows],
  );

  const handleRowSave = useCallback(
    async (rowKey: string) => {
      const editedValues = tableState.editingRows.get(rowKey);
      const subRowKeys = getSubRowKeysForParent(rowKey);
      const hasSubRowEdits = subRowKeys.length > 0;
      if (!editedValues && !hasSubRowEdits) return;

      const original = displayData.find((r, idx) => {
        const rec = r as Record<string, unknown>;
        return resolveRowKey(rec, idx) === rowKey;
      }) as Record<string, unknown> | undefined;

      let mergedRow: Record<string, unknown> = original
        ? { ...original, ...(editedValues ?? {}) }
        : { ...(editedValues ?? {}) };

      const subField = internalSchema.features.rows.subRows?.dataField;
      if (subField && subRowKeys.length > 0) {
        const originalSubRows = (mergedRow[subField] as unknown[] | undefined) ?? [];
        const updatedSubRows = originalSubRows.map((sr, idx) => {
          const subKey = `${rowKey}.${idx}`;
          const subEdits = tableState.editingRows.get(subKey);
          return subEdits ? { ...(sr as Record<string, unknown>), ...subEdits } : sr;
        });
        mergedRow = { ...mergedRow, [subField]: updatedSubRows };
      }

      if (!applyRowValidation([{ rowKey, mergedRow }])) return;

      const confirmed = await confirmSave();
      if (!confirmed) return;

      const cleanupEditing = () => {
        stateActions.applyEdit(rowKey);
        if (subRowKeys.length > 0) {
          stateActions.cancelEditingBatch(subRowKeys);
        }
      };

      if (onSave) {
        setInternalSaveLoading(true);
        try {
          await onSave([mergedRow], tableState.data);
          cleanupEditing();
        } finally {
          setInternalSaveLoading(false);
          dismissSaveConfirm();
        }
      } else if (internalSchema.dataSource?.saveEndpoint) {
        const ds = internalSchema.dataSource;
        const endpoint = ds.saveEndpoint!;
        const payload = applyPayloadMapper([mergedRow], ds.savePayloadMapper);
        setInternalSaveLoading(true);
        try {
          const result = await executeSaveAction(
            payload,
            endpoint,
            ds.saveMethod,
            ds.onSaveSuccess,
            ds.saveSuccessMessage
          );
          const saveOk = result?.ok === true;
          if (saveOk) {
            cleanupEditing();
            stateActions.clearServerErrors();
          } else {
            const fieldErrors: Array<{ sNo: string | null; field: string; message: string }> =
              result?.errors ?? [];
            const hasInlineErrors = fieldErrors.some((e) => e.field);
            if (hasInlineErrors) {
              const errorMap = new Map<string, Record<string, string>>();
              for (const e of fieldErrors) {
                if (!e.field) continue;
                const targetKey = e.sNo != null && e.sNo.includes('.')
                  ? `${rowKey}.${e.sNo.split('.')[1]}`
                  : rowKey;
                const existing = errorMap.get(targetKey) ?? {};
                existing[e.field] = e.message;
                errorMap.set(targetKey, existing);
              }
              stateActions.setServerErrors(errorMap);
            }
          }
        } finally {
          setInternalSaveLoading(false);
          dismissSaveConfirm();
        }
      } else {
        dismissSaveConfirm();
        cleanupEditing();
      }
    },
    [
      tableState.editingRows,
      stateActions,
      onSave,
      displayData,
      resolveRowKey,
      internalSchema.dataSource,
      internalSchema.features.rows.subRows?.dataField,
      tableState.data,
      executeSaveAction,
      confirmSave,
      dismissSaveConfirm,
      getSubRowKeysForParent,
      applyRowValidation,
    ]
  );

  const handleRowCancel = useCallback(
    (rowKey: string) => {
      const subRowKeys = getSubRowKeysForParent(rowKey);
      if (subRowKeys.length > 0) {
        stateActions.cancelEditingBatch([rowKey, ...subRowKeys]);
      } else {
        stateActions.cancelEditing(rowKey);
      }
    },
    [stateActions, getSubRowKeysForParent]
  );

  const handleBulkSave = useCallback(async () => {
    const subField = internalSchema.features.rows.subRows?.dataField;
    const parentKeys = Array.from(tableState.editingRows.keys()).filter((k) => !k.includes('.'));

    const mergedRows: Record<string, unknown>[] = parentKeys.map((rowKey) => {
      const parentEdits = tableState.editingRows.get(rowKey) ?? {};
      const original = displayData.find((r, idx) => {
        const rec = r as Record<string, unknown>;
        return resolveRowKey(rec, idx) === rowKey;
      }) as Record<string, unknown> | undefined;

      let mergedRow: Record<string, unknown> = original
        ? { ...original, ...parentEdits }
        : { ...parentEdits };

      if (subField) {
        const subRowKeys = getSubRowKeysForParent(rowKey);
        if (subRowKeys.length > 0) {
          const originalSubRows = (mergedRow[subField] as unknown[] | undefined) ?? [];
          const updatedSubRows = originalSubRows.map((sr, idx) => {
            const subKey = `${rowKey}.${idx}`;
            const subEdits = tableState.editingRows.get(subKey);
            return subEdits ? { ...(sr as Record<string, unknown>), ...subEdits } : sr;
          });
          mergedRow = { ...mergedRow, [subField]: updatedSubRows };
        }
      }
      return mergedRow;
    });

    const validationRows = parentKeys.map((rowKey, index) => ({
      rowKey,
      mergedRow: mergedRows[index],
    }));
    if (!applyRowValidation(validationRows)) return;

    const confirmed = await confirmSave();
    if (!confirmed) return;

    if (onSave) {
      setInternalSaveLoading(true);
      try {
        await onSave(mergedRows, tableState.data);
        stateActions.cancelAllEditing();
        stateActions.clearServerErrors();
      } finally {
        setInternalSaveLoading(false);
        dismissSaveConfirm();
      }
      return;
    } else if (internalSchema.dataSource?.saveEndpoint) {
      const ds = internalSchema.dataSource;
      const endpoint = ds.saveEndpoint!;
      const payload = applyPayloadMapper(mergedRows, ds.savePayloadMapper);
      setInternalSaveLoading(true);
      try {
        const result = await executeSaveAction(
          payload,
          endpoint,
          ds.saveMethod,
          ds.onSaveSuccess,
          ds.saveSuccessMessage
        );
        const saveOk = result?.ok === true;
        if (saveOk) {
          stateActions.cancelAllEditing();
          stateActions.clearServerErrors();
        } else {
          const fieldErrors: Array<{ sNo: string | null; field: string; message: string }> =
            result?.errors ?? [];
          const hasInlineErrors = fieldErrors.some((e) => e.field && e.sNo != null);
          if (hasInlineErrors) {
            const editingKeys = parentKeys;
            const errorMap = new Map<string, Record<string, string>>();
            for (const err of fieldErrors) {
              if (!err.field) continue;
              let targetKey: string | undefined;
              if (err.sNo != null && err.sNo.includes('.')) {
                const [parentIdx, subIdx] = err.sNo.split('.');
                const parentKey = editingKeys[Number(parentIdx)];
                targetKey = parentKey ? `${parentKey}.${subIdx}` : editingKeys[0];
              } else {
                targetKey =
                  err.sNo != null && editingKeys[Number(err.sNo)]
                    ? editingKeys[Number(err.sNo)]
                    : editingKeys[0];
              }
              if (!targetKey) continue;
              const existing = errorMap.get(targetKey) ?? {};
              existing[err.field] = err.message;
              errorMap.set(targetKey, existing);
            }
            stateActions.setServerErrors(errorMap);
          }
        }
      } finally {
        setInternalSaveLoading(false);
        dismissSaveConfirm();
      }
      return;
    }
    dismissSaveConfirm();
    stateActions.cancelAllEditing();
  }, [
    onSave,
    stateActions,
    internalSchema.dataSource,
    internalSchema.features.rows.subRows?.dataField,
    tableState.data,
    tableState.editingRows,
    displayData,
    resolveRowKey,
    getSubRowKeysForParent,
    executeSaveAction,
    confirmSave,
    dismissSaveConfirm,
    applyRowValidation,
  ]);

  return {
    internalSaveLoading,
    effectiveSaveLoading,
    pendingSaveConfirm,
    handleRowSave,
    handleRowCancel,
    handleBulkSave,
  };
}
